import { ActionIconProps, Mark, Text, UnstyledButton } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, forwardRef } from "react";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { deletePergunta } from "../../firestore";
import { QUERY_KEY } from "../perguntas-table/PerguntasTable";

type RemoveButtonProperties = PropsWithChildren<
  ActionIconProps & { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; pergunta: TPergunta }
>;
type TDeleteResponse = Awaited<ReturnType<typeof deletePergunta>>;

const RemovePerguntaButton = forwardRef<HTMLButtonElement, RemoveButtonProperties>(
  ({ pergunta, children, ...properties }, reference) => {
    const queryClient = useQueryClient();
    const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

    const { mutate: deletePerguntaMutation, isPending } = useMutation<TDeleteResponse, Error>({
      mutationFn: () => {
        if (!activeEmpresaId) {
          throw new Error("Empresa não selecionada");
        }
        return deletePergunta(activeEmpresaId, pergunta);
      },
      onSuccess() {
        return queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
      onError(error) {
        captureException(error, true);
      }
    });

    /**
     * Handles the click event on the button.
     * @param event - The event that triggered the click.
     */
    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();

      if (properties.onClick) {
        properties.onClick(event);
      }

      modals.openConfirmModal({
        title: "Remover pergunta",
        centered: true,
        children: (
          <Text size="sm">
            Tem certeza que deseja remover a pergunta <Mark>{pergunta.nome}</Mark>
          </Text>
        ),
        labels: {
          confirm: "Sim",
          cancel: "Não"
        },
        confirmProps: { color: "red" },
        onConfirm: () => {
          void deletePerguntaMutation();
        }
      });
    }

    return (
      <UnstyledButton disabled={isPending} ref={reference} {...properties} onClick={handleClick}>
        {children}
      </UnstyledButton>
    );
  }
);

RemovePerguntaButton.displayName = "RemovePerguntaButton";

export default RemovePerguntaButton;
